import { useId, type ReactNode } from 'react'
import styles from './fields.module.css'

type Props = {
  label: ReactNode
  error?: ReactNode
  supportText?: ReactNode
  required?: boolean
  children: (ids: {
    id: string
    describedBy?: string
    required?: boolean
  }) => ReactNode
}

/**
 * Campo rotulado para os formulários da conta (S17/S18).
 * Mesmo contrato dos componentes ACUL: label visível, erro com `role="alert"`
 * ligado ao controle por `aria-describedby`.
 */
export function Field({ label, error, supportText, required, children }: Props) {
  const id = useId()
  const errorId = error ? `${id}-error` : undefined
  const supportId = supportText ? `${id}-support` : undefined
  const describedBy = [errorId, supportId].filter(Boolean).join(' ') || undefined
  return (
    <div className={styles.field}>
      <label
        className={styles.label}
        htmlFor={id}
        data-required={required ? 'true' : undefined}
      >
        {label}
      </label>
      {children({ id, describedBy, required })}
      {error ? (
        <p id={errorId} className={styles.fieldError} role="alert">
          {error}
        </p>
      ) : null}
      {supportText ? (
        <p id={supportId} className={styles.supportText}>
          {supportText}
        </p>
      ) : null}
    </div>
  )
}

export function TextInput({
  id,
  error,
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement> & { id: string; error?: boolean }) {
  return (
    <input
      id={id}
      className={styles.input}
      aria-invalid={error ? true : undefined}
      {...rest}
    />
  )
}

export function SelectInput({
  id,
  children,
  ...rest
}: React.SelectHTMLAttributes<HTMLSelectElement> & { id: string }) {
  return (
    <select id={id} className={styles.select} {...rest}>
      {children}
    </select>
  )
}

export const fieldStyles = styles
